import { setProblemLookup, problemLookupFrom } from '../api/problems';
import { LOCALES, type Lang, type Params } from '../i18n';
import { consoleEn } from './en';

/** A status tone: the chip and KPI colour (nyk-tone-* / nyk-kpi-* classes). */
export type Tone = 'green' | 'red' | 'amber' | 'grey' | 'blue';

/** What the console components need from the app: its translations (catalogue keys listed in `ConsoleKey`) and extras. */
export interface ConsoleBinding {
  /** The app's `useI18n` (re-renders on a language switch). */
  useI18n: () => { t: (key: never, params?: Params) => string; lang: Lang };
  /** The app's `i18n` for code outside components. */
  i18n: { t: (key: never, params?: Params) => string; readonly lang: Lang };
  /** App-specific status → tone entries on top of the shared ones. */
  tones?: Record<string, Tone>;
}

/**
 * The catalogue keys the console components read; every app catalogue that binds the console must have them:
 * `common.tryAgain`, `common.about`, `common.loadMore`, `common.ofTotal`, `error.noConnection`, `error.unreachable`,
 * `error.generic`, `time.justNow`, `time.minAgo`, `time.hAgo`, `shell.navigation`, `shell.openMenu`, `shell.closeMenu`,
 * `shell.expand`, `shell.collapse`, `shell.themeLabel`, `shell.theme.<choice>`, `common.dismiss`, `common.removeValue`, `pager.*`, and `status.<code>`
 * for status labels (optional per code); `consoleEn` has every English text, the grid's and developer panel's too.
 */
export type ConsoleKey =
  | 'common.tryAgain'
  | 'common.about'
  | 'common.loadMore'
  | 'common.ofTotal'
  | 'error.noConnection'
  | 'error.unreachable'
  | 'error.generic'
  | 'time.justNow'
  | 'time.minAgo'
  | 'time.hAgo'
  | 'shell.navigation'
  | 'shell.openMenu'
  | 'shell.closeMenu'
  | 'shell.expand'
  | 'shell.collapse'
  | 'shell.themeLabel'
  | 'shell.theme.dark'
  | 'shell.theme.light'
  | 'shell.theme.system'
  | 'common.dismiss'
  | 'common.removeValue'
  | 'pager.previous'
  | 'pager.next'
  | 'pager.range'
  | 'pager.none';

type T = (key: string, params?: Params) => string;

let bound: {
  useI18n: () => { t: T; lang: Lang };
  i18n: { t: T; readonly lang: Lang };
  tones: Record<string, Tone>;
} | null = null;

/**
 * Binds the console components to the app's translations; call once before the first render (main.tsx). Apps that
 * only speak English need not call it.
 */
export function bindConsole(binding: ConsoleBinding) {
  bound = {
    useI18n: binding.useI18n as unknown as () => { t: T; lang: Lang },
    i18n: binding.i18n as unknown as { t: T; readonly lang: Lang },
    tones: binding.tones ?? {},
  };
  // Problem responses follow the app's language too (`problems.<code>` in its catalogue, ADR 0031).
  setProblemLookup(problemLookupFrom((key, params) => bound!.i18n.t(key, params)));
}

/** Without `bindConsole` the components speak English (`consoleEn`): an English-only app binds nothing. */
const english: T = (key, params) =>
  ((consoleEn as Record<string, string>)[key] ?? key).replace(/\{(\w+)\}/g, (m, name: string) =>
    params?.[name] === undefined || params[name] === null ? m : String(params[name])
  );
const unbound = {
  useI18n: () => ({ t: english, lang: 'en' as Lang }),
  i18n: { t: english, lang: 'en' as Lang },
  tones: {} as Record<string, Tone>,
};

function current() {
  return bound ?? unbound;
}

/** The app's translations in a component (re-renders on a language switch). */
export const useConsoleI18n = () => current().useI18n();

/** The app's translations outside components. */
export const ct: T = (key, params) => current().i18n.t(key, params);

/** The message for a key built at runtime, or `fallback` when no catalogue has it. */
export function tOr(key: string, fallback: string): string {
  const text = ct(key);
  return text === key ? fallback : text;
}

/** BCP 47 locale of the UI language. */
export const locale = () => LOCALES[current().i18n.lang];

export const extraTones = () => current().tones;
